/* eslint-env browser */

/**
 * home-surface.js — the hero's contour map reacts to the pointer like the
 * surface of water.
 *
 * The contour lines are the visible surface; the pointer is a hand moving
 * through it. Lines near the pointer bend away and curl around it, and
 * settle back slowly once it moves on or leaves.
 *
 * Cost control:
 * - Mouse and trackpad only ((hover: hover) and (pointer: fine)). Touch
 *   devices keep the plain drifting map.
 * - The loop runs only while something moves. At rest nothing runs.
 * - Only lines with a point inside the reach are rebuilt. The influence
 *   falls to exactly zero at the reach, so far lines never change.
 * - Near the pointer, long segments are split into short ones before they
 *   move, so a bend reads as a curve, not a corner. The rest of each line
 *   keeps its original (simplified) points.
 * Reduced motion: nothing runs.
 */

(function (window, document) {
    'use strict';

    var hero = document.querySelector('.home-hero');
    var svg = hero && hero.querySelector('.home-hero-currents');
    if (!svg) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    // In viewBox units (1600 x 1000).
    var REACH = 320;      // influence ends here
    var PUSH = 80;        // outward push at the centre
    var SWIRL = 36;       // sideways curl at the centre
    var STEP = 6;         // max segment length inside the reach
    // Per-frame easing. Low values = slow, viscous water.
    var FOLLOW = 0.05;    // how fast the disturbance follows the pointer
    var RISE = 0.035;     // how fast it builds when the pointer enters
    var FALL = 0.018;     // how fast the surface settles after it leaves

    var lines = [].map.call(svg.querySelectorAll('path'), function (el) {
        var pts = el.getAttribute('d').replace(/^M/, '').trim().split(/\s+/).map(function (s) {
            var xy = s.split(',');
            return [+xy[0], +xy[1]];
        });
        var box = [Infinity, Infinity, -Infinity, -Infinity];
        pts.forEach(function (q) {
            box[0] = Math.min(box[0], q[0]);
            box[1] = Math.min(box[1], q[1]);
            box[2] = Math.max(box[2], q[0]);
            box[3] = Math.max(box[3], q[1]);
        });
        return { el: el, pts: pts, box: box, d: el.getAttribute('d'), moved: false };
    });

    var p = { x: 0, y: 0, tx: 0, ty: 0, s: 0, ts: 0 };
    var R2 = REACH * REACH;

    // Client coordinates → viewBox (the SVG uses xMidYMid slice).
    function toView(cx, cy) {
        var r = svg.getBoundingClientRect();
        var k = Math.max(r.width / 1600, r.height / 1000);
        return [
            (cx - r.left - (r.width - 1600 * k) / 2) / k,
            (cy - r.top - (r.height - 1000 * k) / 2) / k
        ];
    }

    // Displacement at a point: push out from the pointer and curl around
    // it. (1 - d²/R²)³ falls smoothly to zero at the reach, with no edge.
    function bend(x, y, out) {
        var dx = x - p.x;
        var dy = y - p.y;
        var d2 = dx * dx + dy * dy;
        if (d2 >= R2) {
            out[0] = x;
            out[1] = y;
            return;
        }
        var u = 1 - d2 / R2;
        var f = u * u * u * p.s;
        var d = Math.sqrt(d2) || 1;
        var nx = dx / d;
        var ny = dy / d;
        out[0] = x + (nx * PUSH - ny * SWIRL) * f;
        out[1] = y + (ny * PUSH + nx * SWIRL) * f;
    }

    function near(line) {
        var b = line.box;
        if (p.x < b[0] - REACH || p.x > b[2] + REACH || p.y < b[1] - REACH || p.y > b[3] + REACH) return false;
        for (var k = 0; k < line.pts.length; k++) {
            var ex = line.pts[k][0] - p.x;
            var ey = line.pts[k][1] - p.y;
            if (ex * ex + ey * ey < R2) return true;
        }
        return false;
    }

    // Rebuild one line: segments that touch the reach are split into
    // STEP-long pieces, then every point is bent.
    var q = [0, 0];
    function rebuild(line) {
        var pts = line.pts;
        var reach = REACH + STEP;
        var d = '';
        for (var k = 0; k < pts.length; k++) {
            var a = pts[k];
            if (k > 0) {
                var z = pts[k - 1];
                var sx = a[0] - z[0];
                var sy = a[1] - z[1];
                var len = Math.sqrt(sx * sx + sy * sy);
                // Distance from the pointer to this segment.
                var t = len ? Math.max(0, Math.min(1, ((p.x - z[0]) * sx + (p.y - z[1]) * sy) / (len * len))) : 0;
                var cx = z[0] + sx * t - p.x;
                var cy = z[1] + sy * t - p.y;
                if (len > STEP && cx * cx + cy * cy < reach * reach) {
                    var n = Math.ceil(len / STEP);
                    for (var j = 1; j < n; j++) {
                        bend(z[0] + sx * j / n, z[1] + sy * j / n, q);
                        d += ' ' + q[0].toFixed(1) + ',' + q[1].toFixed(1);
                    }
                }
            }
            bend(a[0], a[1], q);
            d += (k ? ' ' : 'M') + q[0].toFixed(1) + ',' + q[1].toFixed(1);
        }
        line.el.setAttribute('d', d);
    }

    var running = false;

    function frame() {
        p.x += (p.tx - p.x) * FOLLOW;
        p.y += (p.ty - p.y) * FOLLOW;
        p.s += (p.ts - p.s) * (p.ts > p.s ? RISE : FALL);

        for (var i = 0; i < lines.length; i++) {
            var line = lines[i];
            if (p.s > 0.002 && near(line)) {
                rebuild(line);
                line.moved = true;
            } else if (line.moved) {
                line.el.setAttribute('d', line.d);
                line.moved = false;
            }
        }

        var still = Math.abs(p.ts - p.s) < 0.002 &&
            Math.abs(p.tx - p.x) < 0.3 && Math.abs(p.ty - p.y) < 0.3;
        if (still) {
            running = false;
            return;
        }
        window.requestAnimationFrame(frame);
    }

    function start() {
        if (running) return;
        running = true;
        window.requestAnimationFrame(frame);
    }

    hero.addEventListener('pointermove', function (e) {
        var v = toView(e.clientX, e.clientY);
        // Entering from rest: start the disturbance where the pointer is,
        // not where it last left.
        if (p.s < 0.002) {
            p.x = v[0];
            p.y = v[1];
        }
        p.tx = v[0];
        p.ty = v[1];
        p.ts = 1;
        start();
    });

    hero.addEventListener('pointerleave', function () {
        p.ts = 0;
        start();
    });

})(window, document);
